import { useQueryClient } from '@tanstack/react-query';
import type { StepProps } from 'antd';
import { Form, Steps } from 'antd';
import { useContext, useMemo, useRef, useState } from 'react';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { useSaveAutoInventory } from 'app/hooks/data/core/extranet/inventory/rooms/useAutoInventory';
import {
  autoInventoryInventoryQuery,
  useAutoInventoryInventory,
} from 'app/hooks/data/core/extranet/inventory/rooms/useAutoInventoryInventory';
import { useDisable } from 'app/hooks/data/core/extranet/inventory/rooms/useDisableAutoInventory';
import {
  AutoInventoryStepsModern,
  ModifierType,
} from 'app/typings/autoInventory';
import { BAR_MODIFIER_TYPE } from 'app/typings/core/extranet/inventory/enums/BarModifierType';
import type { BarModifierType } from 'app/typings/core/extranet/inventory/enums/BarModifierType';
import type {
  AutoInventoryForm,
  AutoInventoryRoomMapping,
} from 'app/typings/core/extranet/inventory/form/autoInventoryForm';
import { formatDateWithoutHour } from 'app/utils/dates';

import { useInventoryState } from '../../../../hooks';
import { useFilteredRooms } from '../../../../hooks/useFilteredRooms';
import type { AutoInventoryFormValues } from '../../types';

import styles from './StepAutoInventoryConfig.module.scss';
import { Bar } from './_components/Bar/Bar';
import { Preview } from './_components/Preview/Preview';
import { RoomMapping } from './_components/RoomMapping/RoomMapping';

const MODERN_STEPS_ORDER: AutoInventoryStepsModern[] = [
  AutoInventoryStepsModern['room-mapping'],
  AutoInventoryStepsModern.bar,
  AutoInventoryStepsModern.preview,
];

const getRoomMappingSignature = (
  roomMappings: AutoInventoryFormValues['roomMappings'] | undefined
): string =>
  (roomMappings ?? [])
    .map((rm) => `${rm?.staycationRoom?.id ?? ''}:${rm?.scrappedRoomId ?? ''}`)
    .sort()
    .join('|');

type StepAutoInventoryConfigProps = {
  handleClose: () => void;
};

export const StepAutoInventoryConfig = ({
  handleClose,
}: StepAutoInventoryConfigProps) => {
  const { hotelId, hotelInfo } = useContext(HotelContext);
  const { sortedRooms } = useFilteredRooms();

  const [currentStep, setCurrentStep] = useState<AutoInventoryStepsModern>(
    AutoInventoryStepsModern['room-mapping']
  );
  const [form] = Form.useForm();

  const { timePeriod } = useInventoryState();

  const hookOptions = useMemo(
    () => ({
      startDate: formatDateWithoutHour(timePeriod.startDate),
      endDate: formatDateWithoutHour(timePeriod.endDate),
    }),
    [timePeriod]
  );

  const { mutateAsync: saveAutoInventory, isLoading: isSaving } =
    useSaveAutoInventory(hotelId, hookOptions);
  const { mutateAsync: disableAutoInventory, isLoading: isDisabling } =
    useDisable(hotelId, hookOptions);

  const queryClient = useQueryClient();
  const { data: previewInventory, isFetching: isPreviewInventoryFetching } =
    useAutoInventoryInventory(hotelId, { enabled: false });

  const lastFetchedMappingRef = useRef<string | null>(null);

  const hotelUseMatchOTAPrice =
    hotelInfo?.stockScrapperBarModifierType === undefined &&
    hotelInfo?.stockScrapperBarModifierValue === undefined;

  const initialValues = useMemo(
    (): AutoInventoryFormValues => ({
      roomMappings: (sortedRooms ?? []).map((room) => ({
        scrappedRoomId: room.bookingcomId,
        scrappedRoomName: room.bookingcomName,
        staycationRoom: {
          id: room.id,
          name: room.name ?? room.categoryName ?? '',
        },
      })),
      matchOTAPrice: !hotelInfo?.stockScrapperEnabled
        ? undefined
        : hotelUseMatchOTAPrice,
      customBar:
        hotelInfo?.stockScrapperEnabled &&
        hotelInfo.stockScrapperBarModifierType
          ? {
              value:
                hotelInfo.stockScrapperBarModifierType === ModifierType.RATE &&
                hotelInfo.stockScrapperBarModifierValue !== undefined
                  ? Math.round(hotelInfo.stockScrapperBarModifierValue * 100)
                  : hotelInfo?.stockScrapperBarModifierValue,
              modifierType:
                hotelInfo?.stockScrapperBarModifierType as unknown as BarModifierType,
            }
          : undefined,
      overrideOpenings: false,
    }),
    [
      hotelInfo?.stockScrapperBarModifierType,
      hotelInfo?.stockScrapperBarModifierValue,
      hotelInfo?.stockScrapperEnabled,
      hotelUseMatchOTAPrice,
      sortedRooms,
    ]
  );

  const handleFinish = async (values: AutoInventoryFormValues) => {
    const payload: AutoInventoryForm = {
      roomMappings: (values.roomMappings ?? [])
        .map((rm): AutoInventoryRoomMapping | null => {
          if (
            !rm?.scrappedRoomId ||
            !rm?.scrappedRoomName ||
            !rm?.staycationRoom
          ) {
            return null;
          }

          return {
            id: rm.staycationRoom.id,
            scrappedRoomId: rm.scrappedRoomId,
            scrappedRoomName: rm.scrappedRoomName,
          };
        })
        .filter((rm): rm is AutoInventoryRoomMapping => rm !== null),
      barModifier:
        values.matchOTAPrice === false && values.customBar?.value
          ? {
              value:
                values.customBar.modifierType === BAR_MODIFIER_TYPE.RATE
                  ? values.customBar.value / 100
                  : values.customBar.value,
              modifierType: values.customBar.modifierType,
            }
          : undefined,
      overrideOpenings: values.overrideOpenings ?? false,
    };

    await saveAutoInventory(payload);

    handleClose();
  };

  const currentStepIndex = MODERN_STEPS_ORDER.indexOf(currentStep);

  const items: StepProps[] = useMemo(() => {
    const getStatus = (step: AutoInventoryStepsModern): StepProps['status'] => {
      const stepIndex = MODERN_STEPS_ORDER.indexOf(step);

      if (currentStepIndex > stepIndex) {
        return 'finish';
      }

      if (currentStepIndex === stepIndex) {
        return 'process';
      }

      return 'wait';
    };

    return [
      {
        title: 'Room mapping',
        status: getStatus(AutoInventoryStepsModern['room-mapping']),
      },
      {
        title: 'BAR',
        status: getStatus(AutoInventoryStepsModern.bar),
      },
      {
        title: 'Preview',
        status: getStatus(AutoInventoryStepsModern.preview),
      },
    ];
  }, [currentStepIndex]);

  const handleStepChange = (value: AutoInventoryStepsModern) => {
    if (value === AutoInventoryStepsModern.preview) {
      const mappingSignature = getRoomMappingSignature(
        form.getFieldValue('roomMappings')
      );
      const mappingChanged = mappingSignature !== lastFetchedMappingRef.current;

      lastFetchedMappingRef.current = mappingSignature;

      void queryClient
        .fetchQuery({
          ...autoInventoryInventoryQuery(hotelId),
          staleTime: mappingChanged ? 0 : Infinity,
        })
        .finally(() => setCurrentStep(value));

      return;
    }

    setCurrentStep(value);
  };

  const handleDeactivate = async () => {
    await disableAutoInventory();
  };

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={handleFinish}
      className={styles.stepAutoInventoryConfig}
    >
      <Steps
        type="navigation"
        size="small"
        items={items}
        className={styles.steps}
        current={currentStepIndex}
      />
      <div className={styles.containerSteps}>
        <div
          style={{
            display:
              currentStep === AutoInventoryStepsModern['room-mapping']
                ? 'contents'
                : 'none',
          }}
        >
          <RoomMapping
            handleStepChange={handleStepChange}
            onDeactivate={handleDeactivate}
            isDisabling={isDisabling}
          />
        </div>
        <div
          style={{
            display:
              currentStep === AutoInventoryStepsModern.bar
                ? 'contents'
                : 'none',
          }}
        >
          <Bar
            handleStepChange={handleStepChange}
            onDeactivate={handleDeactivate}
            isDisabling={isDisabling}
            isNextLoading={isPreviewInventoryFetching}
          />
        </div>
        <div
          style={{
            display:
              currentStep === AutoInventoryStepsModern.preview
                ? 'contents'
                : 'none',
          }}
        >
          <Preview
            handleStepChange={handleStepChange}
            onDeactivate={handleDeactivate}
            isSaving={isSaving}
            isDisabling={isDisabling}
            previewInventory={previewInventory}
            isLoading={isPreviewInventoryFetching}
          />
        </div>
      </div>
    </Form>
  );
};
